import React, { useState, useMemo, useEffect } from "react";
import { Clock, Calendar, AlertTriangle, CheckCircle, XCircle, FileText } from "lucide-react";
import PayrollTable from "../../Components/PayrollTable/PayrollTable";
import axios from "axios";

const BaseUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";

// Helper function to format UTC time string to HH:MM AM/PM
const formatTimeToHHMM = (isoString) => {
  if (!isoString) return "-";
  try {
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return "-";

    let hours = date.getUTCHours();
    const minutes = String(date.getUTCMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    hours = hours ? hours : 12;
    return `${String(hours).padStart(2, "0")}:${minutes} ${ampm}`;
  } catch (error) {
    console.error("Error formatting time:", error);
    return "-";
  }
};

// Helper function to format date string to DD MMM YYYY (Day)
const formatDateLong = (dateStr) => {
  if (!dateStr) return "-";
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "-";
    const day = String(d.getUTCDate()).padStart(2, "0");
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const month = months[d.getUTCMonth()];
    const year = d.getUTCFullYear();
    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const dayOfWeek = days[d.getUTCDay()];
    return `${day} ${month} ${year} (${dayOfWeek})`;
  } catch (e) {
    return dateStr;
  }
};

const AttendanceDetailedBreakdown = ({ filteredPunches, fromDate, toDate, onDataProcessed, checkInTime, checkOutTime }) => {
  const [stdCheckIn, setStdCheckIn] = useState(checkInTime || "10:00");
  const [stdCheckOut, setStdCheckOut] = useState(checkOutTime || "19:00");
  const [activeTab, setActiveTab] = useState("all");
  const [holidays, setHolidays] = useState([]);

  useEffect(() => {
    const fetchHolidays = async () => {
      try {
        const res = await axios.get(`${BaseUrl}/Chat/holidays`);
        setHolidays(res.data?.data || []);
      } catch (err) {
        console.error("Error fetching holidays in AttendanceDetailedBreakdown:", err);
      }
    };
    fetchHolidays();
  }, []);

  const holidayMap = useMemo(() => {
    const map = new Map();
    if (Array.isArray(holidays)) {
      holidays.forEach((h) => {
        if (h.dateStr) map.set(h.dateStr, h.name || "Company Holiday");
      });
    }
    return map;
  }, [holidays]);

  useEffect(() => {
    if (checkInTime) setStdCheckIn(checkInTime);
    if (checkOutTime) setStdCheckOut(checkOutTime);
  }, [checkInTime, checkOutTime]);

  // Helper to generate all dates in the selected range
  const dateList = useMemo(() => {
    if (!fromDate || !toDate) return [];
    const dates = [];
    try {
      const start = new Date(fromDate);
      const end = new Date(toDate);
      start.setUTCHours(0, 0, 0, 0);
      end.setUTCHours(0, 0, 0, 0);

      const current = new Date(start);
      while (current <= end) {
        dates.push(current.toISOString().slice(0, 10));
        current.setUTCDate(current.getUTCDate() + 1);
      }
    } catch (e) {
      console.error("Error generating dates range:", e);
    }
    return dates;
  }, [fromDate, toDate]);

  // Process details for each day
  const processedDays = useMemo(() => {
    if (dateList.length === 0) return [];

    const punchesByDay = {};
    if (Array.isArray(filteredPunches)) {
      filteredPunches.forEach((punch) => {
        if (!punch) return;
        const inDateKey = punch.InDate ? punch.InDate.slice(0, 10) : null;
        const outDateKey = punch.OutDate ? punch.OutDate.slice(0, 10) : null;
        const dateKey = inDateKey || outDateKey;

        if (dateKey) {
          if (!punchesByDay[dateKey]) {
            punchesByDay[dateKey] = [];
          }
          punchesByDay[dateKey].push(punch);
        }
      });
    }

    const [stdInHour, stdInMin] = stdCheckIn.split(":").map(Number);
    const stdInTotalMinutes = stdInHour * 60 + stdInMin;

    const [stdOutHour, stdOutMin] = stdCheckOut.split(":").map(Number);
    const stdOutTotalMinutes = stdOutHour * 60 + stdOutMin;

    return dateList.map((dateStr) => {
      const dayDate = new Date(dateStr);
      const isSunday = dayDate.getUTCDay() === 0;

      const punches = punchesByDay[dateStr] || [];

      // Find first In and last Out
      let firstIn = null;
      let lastOut = null;

      punches.forEach((p) => {
        if (p.InDate) {
          const d = new Date(p.InDate);
          if (!firstIn || d < new Date(firstIn)) {
            firstIn = p.InDate;
          }
        }
        if (p.OutDate) {
          const d = new Date(p.OutDate);
          if (!lastOut || d > new Date(lastOut)) {
            lastOut = p.OutDate;
          }
        }
      });

      let lateMinutes = 0;
      let earlyMinutes = 0;
      let status = "Absent";

      if (firstIn || lastOut) {
        status = "Present";

        // Calculate late minutes
        if (firstIn) {
          const inDateObj = new Date(firstIn);
          const inTotalMin = inDateObj.getUTCHours() * 60 + inDateObj.getUTCMinutes();
          if (inTotalMin > stdInTotalMinutes) {
            lateMinutes = inTotalMin - stdInTotalMinutes;
          }
        }

        // Calculate early exit minutes
        if (lastOut) {
          const outDateObj = new Date(lastOut);
          const outTotalMin = outDateObj.getUTCHours() * 60 + outDateObj.getUTCMinutes();
          if (outTotalMin < stdOutTotalMinutes) {
            earlyMinutes = stdOutTotalMinutes - outTotalMin;
          }
        }
      } else if (holidayMap.has(dateStr)) {
        const hName = holidayMap.get(dateStr);
        status = hName ? `Holiday (${hName})` : "Holiday";
      } else if (isSunday) {
        status = "Weekend Off";
      }

      return {
        dateStr,
        isSunday,
        firstIn,
        lastOut,
        lateMinutes,
        earlyMinutes,
        status,
      };
    });
  }, [dateList, filteredPunches, stdCheckIn, stdCheckOut, holidayMap]);

  useEffect(() => {
    if (typeof onDataProcessed === "function") {
      onDataProcessed(processedDays);
    }
  }, [processedDays, onDataProcessed]);

  // Summaries
  const summaries = useMemo(() => {
    let presentCount = 0;
    let absentCount = 0;
    let lateCount = 0;
    let earlyCount = 0;
    let totalLateMinutes = 0;
    let totalEarlyMinutes = 0;
    let totalWorkedMinutes = 0;

    processedDays.forEach((day) => {
      if (day.status === "Present") {
        presentCount++;
        if (day.lateMinutes > 0) {
          lateCount++;
          totalLateMinutes += day.lateMinutes;
        }
        if (day.earlyMinutes > 0 && day.lastOut) {
          earlyCount++;
          totalEarlyMinutes += day.earlyMinutes;
        }
        
        // Calculate worked minutes for this day
        if (day.firstIn && day.lastOut) {
          const diffMs = new Date(day.lastOut) - new Date(day.firstIn);
          const diffMins = Math.floor(diffMs / (1000 * 60));
          if (diffMins > 0) {
            totalWorkedMinutes += diffMins;
          }
        }
      } else if (day.status === "Absent") {
        absentCount++;
      }
    });

    return {
      present: presentCount,
      absent: absentCount,
      late: lateCount,
      early: earlyCount,
      totalLateMinutes,
      totalEarlyMinutes,
      totalWorkedMinutes,
    };
  }, [processedDays]);

  // Filtered days list based on active tab
  const filteredDays = useMemo(() => {
    return processedDays.filter((day) => {
      if (activeTab === "all") return true;
      if (activeTab === "late") return day.status === "Present" && day.lateMinutes > 0;
      if (activeTab === "early") return day.status === "Present" && day.earlyMinutes > 0 && day.lastOut;
      if (activeTab === "absent") return day.status === "Absent";
      return true;
    });
  }, [processedDays, activeTab]);

  const formatDurationText = (totalMinutes) => {
    if (totalMinutes <= 0) return "-";
    const hrs = Math.floor(totalMinutes / 60);
    const mins = totalMinutes % 60;
    return hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`;
  };

  const formatTotalDurationText = (totalMinutes) => {
    if (!totalMinutes || totalMinutes <= 0) return "0m";
    const hrs = Math.floor(totalMinutes / 60);
    const mins = totalMinutes % 60;
    return hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`;
  };

  // Columns definition for the project's PayrollTable component
  const columns = [
    { field: "date", headerName: "Date & Day", width: 180, align: "center" },
    { field: "in", headerName: "Punch In", width: 140, align: "center" },
    { field: "out", headerName: "Punch Out", width: 140, align: "center" },
    { field: "late", headerName: "Late Duration", width: 130, align: "center" },
    { field: "early", headerName: "Early Exit", width: 130, align: "center" },
    { field: "status", headerName: "Status", width: 130, align: "center" }
  ];

  // Map rows for the custom table
  const rows = useMemo(() => {
    return filteredDays.map((day, index) => {
      let badgeClass = "";
      let statusLabel = day.status;

      if (day.status === "Present") {
        if (day.lateMinutes > 0 && day.earlyMinutes > 0 && day.lastOut) {
          badgeClass = "bg-amber-50 text-amber-700 border border-amber-200/50";
          statusLabel = "Late & Early Exit";
        } else if (day.lateMinutes > 0) {
          badgeClass = "bg-amber-50 text-amber-700 border border-amber-200/50";
          statusLabel = "Late Arrival";
        } else if (day.earlyMinutes > 0 && day.lastOut) {
          badgeClass = "bg-rose-50 text-rose-700 border border-rose-200/50";
          statusLabel = "Early Exit";
        } else {
          badgeClass = "bg-emerald-50 text-emerald-700 border border-emerald-200/50";
          statusLabel = "On Time";
        }
      } else if (day.status === "Weekend Off" || day.status === "Weekly Off") {
        badgeClass = "bg-blue-50/50 text-blue-600 border border-blue-100/50";
      } else if (day.status === "Holiday" || (typeof day.status === 'string' && day.status.startsWith("Holiday"))) {
        badgeClass = "bg-emerald-50 text-emerald-700 border border-emerald-200/50";
      } else {
        badgeClass = "bg-slate-50 text-slate-500 border border-slate-200/50";
      }

      return {
        id: day.dateStr || index,
        date: (
          <span className="font-bold text-slate-700 text-[13px]">
            {formatDateLong(day.dateStr)}
          </span>
        ),
        in: day.firstIn ? (
          <div className="inline-flex items-center gap-1.5 bg-emerald-50/30 border border-emerald-100/50 rounded-xl px-3 py-1 text-slate-700 font-bold text-[13px] hover:bg-white transition-colors">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
            {formatTimeToHHMM(day.firstIn)}
          </div>
        ) : (
          <span className="text-slate-350 font-bold">-</span>
        ),
        out: day.lastOut ? (
          <div className="inline-flex items-center gap-1.5 bg-blue-50/30 border border-blue-100/50 rounded-xl px-3 py-1 text-slate-700 font-bold text-[13px] hover:bg-white transition-colors">
            <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse"></span>
            {formatTimeToHHMM(day.lastOut)}
          </div>
        ) : day.firstIn ? (
          <span className="text-amber-500 font-black text-[10px] uppercase tracking-wider bg-amber-50 px-2.5 py-1 rounded-xl border border-amber-100/50">
            Missing Out
          </span>
        ) : (
          <span className="text-slate-350 font-bold">-</span>
        ),
        late: day.lateMinutes > 0 ? (
          <span className="inline-block text-amber-700 font-black bg-amber-50 px-2.5 py-1 rounded-xl text-[11px] border border-amber-100/50 tracking-tight">
            {formatDurationText(day.lateMinutes)}
          </span>
        ) : (
          <span className="text-slate-350 font-bold">-</span>
        ),
        early: day.earlyMinutes > 0 && day.lastOut ? (
          <span className="inline-block text-rose-700 font-black bg-rose-50 px-2.5 py-1 rounded-xl text-[11px] border border-rose-100/50 tracking-tight">
            {formatDurationText(day.earlyMinutes)}
          </span>
        ) : (
          <span className="text-slate-350 font-bold">-</span>
        ),
        status: (
          <span className={`inline-block px-3 py-1 text-[9px] font-black rounded-full border uppercase tracking-wider ${badgeClass}`}>
            {statusLabel}
          </span>
        )
      };
    });
  }, [filteredDays]);

  return (
    <div className="bg-gradient-to-br from-white to-slate-50/70 rounded-3xl shadow-[0_15px_40px_rgba(0,0,0,0.03)] border border-slate-100 p-6 md:p-8 mt-10 transition-all duration-300">
      {/* Header Panel */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 border-b border-slate-100 pb-6 mb-8">
        <div>
          <div className="flex items-center gap-3">
            <span className="p-2.5 bg-blue-50/80 text-blue-600 rounded-2xl border border-blue-100/50 shadow-sm flex items-center justify-center">
              <FileText className="w-5 h-5" />
            </span>
            <div>
              <h2 className="text-xl md:text-2xl font-black text-slate-800 tracking-tight">
                Attendance Breakdown
              </h2>
              <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">
                Detailed Log & Analysis
              </p>
            </div>
          </div>
        </div>

        {/* Static Timings Display */}
        <div className="flex flex-wrap items-center gap-4 bg-slate-50 border border-slate-100 px-4 py-2.5 rounded-2xl">
          <div className="flex items-center gap-1.5 text-xs font-bold text-slate-600">
            <Clock className="w-3.5 h-3.5 text-slate-400" />
            <span>Std Check-In:</span>
            <span className="text-slate-800 bg-white px-2 py-0.5 rounded-lg border border-slate-100 text-[11px] font-bold">10:00 AM</span>
          </div>
          <div className="w-px h-3.5 bg-slate-200 hidden sm:block"></div>
          <div className="flex items-center gap-1.5 text-xs font-bold text-slate-600">
            <Clock className="w-3.5 h-3.5 text-slate-400" />
            <span>Std Check-Out:</span>
            <span className="text-slate-800 bg-white px-2 py-0.5 rounded-lg border border-slate-100 text-[11px] font-bold">07:00 PM</span>
          </div>
        </div>
      </div>

      {/* Summary Analytics Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5 mb-8">
        {/* Present Days Card */}
        <div className="bg-gradient-to-br from-emerald-50/50 to-teal-50/30 border border-emerald-100/60 rounded-2xl p-5 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md hover:shadow-emerald-500/5 group">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-emerald-900 uppercase tracking-wider">Present Days</span>
            <span className="p-1.5 bg-emerald-100/50 text-emerald-600 rounded-lg group-hover:scale-105 transition-transform">
              <CheckCircle className="w-4 h-4" />
            </span>
          </div>
          <div className="mt-3 flex items-baseline gap-1">
            <span className="text-2xl font-bold text-slate-900 tracking-tight">{summaries.present}</span>
            <span className="text-xs font-semibold text-emerald-600/80">Days</span>
          </div>
          <div className="mt-2 pt-2 border-t border-emerald-100/30 text-xs font-semibold text-slate-600 group-hover:text-slate-700 transition-colors">
            Total Worked: <span className="font-bold text-slate-700">{formatTotalDurationText(summaries.totalWorkedMinutes)}</span>
          </div>
        </div>

        {/* Late Arrivals Card */}
        <div className="bg-gradient-to-br from-amber-50/50 to-orange-50/30 border border-amber-100/60 rounded-2xl p-5 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md hover:shadow-amber-500/5 group">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-amber-900 uppercase tracking-wider">Late Arrivals</span>
            <span className="p-1.5 bg-amber-100/50 text-amber-600 rounded-lg group-hover:scale-105 transition-transform">
              <Clock className="w-4 h-4" />
            </span>
          </div>
          <div className="mt-3 flex items-baseline gap-1">
            <span className="text-2xl font-bold text-slate-900 tracking-tight">{summaries.late}</span>
            <span className="text-xs font-semibold text-amber-600/80">Days</span>
          </div>
          <div className="mt-2 pt-2 border-t border-amber-100/30 text-xs font-semibold text-slate-600 group-hover:text-slate-700 transition-colors">
            Total Late: <span className="font-bold text-amber-800">{formatTotalDurationText(summaries.totalLateMinutes)}</span>
          </div>
        </div>

        {/* Early Exits Card */}
        <div className="bg-gradient-to-br from-rose-50/50 to-pink-50/30 border border-rose-100/60 rounded-2xl p-5 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md hover:shadow-rose-500/5 group">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-rose-900 uppercase tracking-wider">Early Exits</span>
            <span className="p-1.5 bg-rose-100/50 text-rose-600 rounded-lg group-hover:scale-105 transition-transform">
              <AlertTriangle className="w-4 h-4" />
            </span>
          </div>
          <div className="mt-3 flex items-baseline gap-1">
            <span className="text-2xl font-bold text-slate-900 tracking-tight">{summaries.early}</span>
            <span className="text-xs font-semibold text-rose-600/80">Days</span>
          </div>
          <div className="mt-2 pt-2 border-t border-rose-100/30 text-xs font-semibold text-slate-600 group-hover:text-slate-700 transition-colors">
            Total Early: <span className="font-bold text-rose-800">{formatTotalDurationText(summaries.totalEarlyMinutes)}</span>
          </div>
        </div>

        {/* Absent Days Card */}
        <div className="bg-gradient-to-br from-slate-50 to-zinc-50 border border-slate-200/50 rounded-2xl p-5 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md group">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-slate-800 uppercase tracking-wider">Absent Days</span>
            <span className="p-1.5 bg-slate-200/60 text-slate-500 rounded-lg group-hover:scale-105 transition-transform">
              <XCircle className="w-4 h-4" />
            </span>
          </div>
          <div className="mt-3 flex items-baseline gap-1">
            <span className="text-2xl font-bold text-slate-900 tracking-tight">{summaries.absent}</span>
            <span className="text-xs font-semibold text-slate-600">Days</span>
          </div>
          <div className="mt-2 pt-2 border-t border-slate-200/30 text-xs font-semibold text-slate-600 group-hover:text-slate-700 transition-colors">
            Unexcused: <span className="font-bold text-slate-700">{summaries.absent} Days</span>
          </div>
        </div>
      </div>

      {/* Tabs Selector */}
      <div className="flex flex-wrap gap-2 mb-6 bg-slate-100/60 p-1.5 rounded-xl w-fit">
        <button
          onClick={() => setActiveTab("all")}
          className={`py-2 px-4 rounded-lg text-xs font-semibold transition-all tracking-wider uppercase ${
            activeTab === "all"
              ? "bg-[#133A69] text-white shadow-sm"
              : "text-slate-750 hover:text-slate-900 hover:bg-white/40"
          }`}
        >
          All Days ({processedDays.length})
        </button>
        <button
          onClick={() => setActiveTab("late")}
          className={`py-2 px-4 rounded-lg text-xs font-semibold transition-all tracking-wider uppercase ${
            activeTab === "late"
              ? "bg-amber-600 text-white shadow-sm shadow-amber-500/10"
              : "text-amber-800 hover:bg-amber-50/50"
          }`}
        >
          Late ({summaries.late})
        </button>
        <button
          onClick={() => setActiveTab("early")}
          className={`py-2 px-4 rounded-lg text-xs font-semibold transition-all tracking-wider uppercase ${
            activeTab === "early"
              ? "bg-rose-600 text-white shadow-sm shadow-rose-500/10"
              : "text-rose-800 hover:bg-rose-50/50"
          }`}
        >
          Early Exits ({summaries.early})
        </button>
        <button
          onClick={() => setActiveTab("absent")}
          className={`py-2 px-4 rounded-lg text-xs font-semibold transition-all tracking-wider uppercase ${
            activeTab === "absent"
              ? "bg-slate-700 text-white shadow-sm"
              : "text-slate-750 hover:text-slate-900 hover:bg-slate-200/50"
          }`}
        >
          Absences ({summaries.absent})
        </button>
      </div>

      {/* Details Table wrapper using custom project PayrollTable */}
      <div className="mt-4">
        <PayrollTable columns={columns} rows={rows} />
      </div>
    </div>
  );
};

export default AttendanceDetailedBreakdown;
